import type { ReactNode, ToggleEventHandler } from 'react';

import { useFirstRenderType } from '#/hooks/useFirstRenderType';
import { richDetailsAttributes } from '#/snippets/rich-details.attributes';
import type { TrackClickedData } from '#/snippets/track-clicked.attributes';
import { trackClickedAttributes } from '#/snippets/track-clicked.attributes';

import { richDetailsStateClosedClass } from './richDetailsClasses';

type Props = {
  id?: string;
  name?: string;
  children: ReactNode;
  className?: string;
  /**
   * If marked as singleton, the details will close whenever another singleton is opened
   */
  singleton?: boolean;
  qaId?: string;
  desktopTriggersOnHover?: boolean;
  trackClickedOpen?: TrackClickedData | null;
  defaultOpen?: boolean;
  onToggle?: ToggleEventHandler<HTMLDetailsElement>;
};

export function RichDetails({
  id,
  name,
  children,
  className,
  qaId,
  desktopTriggersOnHover,
  singleton,
  trackClickedOpen,
  defaultOpen,
  onToggle,
}: Props) {
  const { firstRenderType } = useFirstRenderType();

  return (
    <details
      id={id}
      name={name}
      className={[
        className,
        // We only set this class manually once, make sure it is also present for lazy-loaded
        // elements and elements created after client-side navigation
        firstRenderType === 'client' && richDetailsStateClosedClass,
      ]
        .filter(Boolean)
        .join(' ')}
      // oxlint-disable-next-line react/jsx-props-no-spreading
      {...richDetailsAttributes({ desktopTriggersOnHover, singleton })}
      // oxlint-disable-next-line react/jsx-props-no-spreading
      {...(trackClickedOpen
        ? trackClickedAttributes(trackClickedOpen)
        : undefined)}
      suppressHydrationWarning
      open={defaultOpen}
      onToggle={onToggle}
      data-qa-id={qaId}
    >
      {children}
    </details>
  );
}
